import React, { useEffect, useState } from "react";
import {
  Card,
  notification,
  Table,
  Typography,
  Input,
  Select,
  Button,
  Space,
  Form,
} from "antd";
import {
  FileTextOutlined,
  UserOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  EditOutlined,
  CloseOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import { AuthContext } from "contexts/AuthContext";
import {
  createReportIssue,
  updateReportIssue,
} from "services/reportIssuesService";
import { createCaseIssue, selectCaseIssues } from "services/caseIssues.service";
import { selectreportIssuePriorities } from "services/reportIssuePriorities.service";
import { CreateNewReportIssueModal } from "components/ModalReportIssue";
import TinyColor from "tinycolor2";
import { formatDateByLanguage } from "utils/formatDateByLanguage";
import { PrimaryButton } from "components/SuperAdminAndBusiness/PrimaryButton";
import { getTranslatedPreliminaryStatus } from "utils/getTranslatedPreliminaryStatus";

const { Text } = Typography;
const { Option } = Select;

interface ComplaintDetailsSectionProps {
  reportData: any;
  refetchReportData: () => void;
}

const ComplaintDetailsSection: React.FC<ComplaintDetailsSectionProps> = ({
  reportData,
  refetchReportData,
}) => {
  const { t } = useTranslation();
  const navigation = useNavigate();
  const { user } = useContext(AuthContext);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<any>({});

  const [openModalNewIssue, setOpenModalNewIssue] = useState<boolean>(false);

  const [caseIssuesOptions, setCaseIssuesOptions] = useState<
    SelectCaseIssueDto[]
  >([]);

  const [reportIssuePrioritiesOptions, setReportIssuePrioritiesOptions] =
    useState<SelectReportIssuePrioritiesDto[]>([]);

  const [form] = Form.useForm();

  const handleNavigateToIssueDetails = (issueUuid: string) => {
    navigation(
      `/${
        user &&
        (user.type === "supervisor" ||
          user.type === "admin" ||
          user.type === "superAdmin")
          ? "business"
          : "analyst"
      }/intake/issue?issue=${issueUuid}`
    );
  };

  const handleCreateNewReportIssue = async () => {
    try {
      const values = form.getFieldsValue();

      let caseIssueUuid = "";
      const caseIssueValue = values.caseIssue;
      const caseIssueIsNew = caseIssueValue.includes("new");

      if (caseIssueIsNew) {
        const formattedCaseIssueValue = caseIssueValue.replace("new-", "");

        const dataToCreate: CreateCaseIssueDto = {
          case: formattedCaseIssueValue,
          description: values.description ?? "",
          active: true,
          departments: values.departments
            ? Array.isArray(values.departments)
              ? values.departments
              : [values.departments]
            : [],
        };

        const response = await createCaseIssue(dataToCreate);

        if (response) {
          caseIssueUuid = response.uuid;
        }
      } else {
        caseIssueUuid = caseIssueValue;
      }

      const dataToCreate: CreateReportIssueDto = {
        caseIssuesUuid: caseIssueUuid,
        reportIssuePrioritiesUuid: values.priority,
        subject: values.subject,
        reportUuid: reportData.uuid,
        summary: values.summary,
        location: values.location,
      };

      const response = await createReportIssue(dataToCreate);

      if (response) {
        setOpenModalNewIssue(false);
        form.resetFields();
        refetchReportData();

        notification.success({
          placement: "bottomRight",
          message: "Success!",
          description: t("complaintIssueCreatedSuccessfully"),
        });
      }
    } catch (error: any) {
      notification.error({
        placement: "bottomRight",
        message:
          error?.response?.data?.message ||
          t("anErrorOccurredDuringTheOperation"),
      });
    }
  };

  const handleUpdateReportIssueData = async (uuid: string, data: any) => {
    try {
      await updateReportIssue(uuid, data);
      notification.success({
        message: "Success!",
        description: "Report issue updated successfully",
        placement: "bottomRight",
      });
      setEditingKey(null);
      setEditingData({});
      refetchReportData();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || "An error occurred";
      notification.error({
        message: "Error!",
        placement: "bottomRight",
        description: errorMessage,
      });
    }
  };

  const handleFetchSelectCaseIssues = async () => {
    try {
      const response = await selectCaseIssues();
      const responsePriorities = await selectreportIssuePriorities();
      setCaseIssuesOptions(response);
      setReportIssuePrioritiesOptions(responsePriorities);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || "An error occurred";
      notification.error({
        message: "Error!",
        description: errorMessage,
      });
    }
  };

  useEffect(() => {
    handleFetchSelectCaseIssues();
  }, []);

  const isEditing = (record: any) => record.uuid === editingKey;

  const edit = (record: any) => {
    setEditingKey(record.uuid);
    setEditingData({
      caseIssuesUuid: record.caseIssuesUuid || "",
      reportIssuePrioritiesUuid: record.reportIssuePrioritiesUuid || "",
      subject: record.subject || "",
    });
  };

  const cancel = () => {
    setEditingKey(null);
    setEditingData({});
  };

  const save = async (record: any) => {
    try {
      await handleUpdateReportIssueData(record.uuid, {
        caseIssuesUuid: editingData.caseIssuesUuid,
        reportIssuePrioritiesUuid: editingData.reportIssuePrioritiesUuid,
        subject: editingData.subject,
      });
    } catch (error) {
      console.error("Error saving:", error);
    }
  };

  const columns = [
    {
      title: t("complaintDetailsSectionTableCaseNumber"),
      dataIndex: "caseNumber",
      key: "caseNumber",
      width: 150,
      render: (_, record: any) => {
        return record.caseNumber ? record.caseNumber : "-";
      },
    },
    {
      title: t("complaintDetailsSectionTableStep"),
      dataIndex: "step",
      key: "step",
      width: 150,
      render: (_, record: any) => {
        return record.step === "intake"
          ? "Intake"
          : record.step === "preliminaryAssessment"
          ? "Preliminary Assessment"
          : record.step === "case"
          ? "Case"
          : "Intake";
      },
    },
    {
      title: t("status"),
      dataIndex: "reportStatus",
      key: "status",
      render: (_, record) => {
        return (
          <span className="inline-block px-2.5 py-0.5 text-sm font-semibold rounded-lg whitespace-nowrap bg-gray-200 text-black">
            {getTranslatedPreliminaryStatus(
              record?.caseStatus ?? record?.preliminarStatus ?? "pending",
              t
            )}
          </span>
        );
      },
    },
    {
      title: t("caseDetails.department"),
      dataIndex: "departments",
      key: "departments",
      render: (_, record) => {
        return (
          <p>
            {record.caseIssues?.departments.length > 0
              ? record.caseIssues?.departments
                  .map((department: any) => department.name)
                  .join(", ")
              : "-"}
          </p>
        );
      },
    },
    {
      title: t("complaintDetailsSectionTableIssueType"),
      dataIndex: "title",
      key: "title",
      width: 150,
      render: (_, record: any) => {
        if (isEditing(record)) {
          return (
            <Select
              value={editingData.caseIssuesUuid}
              onChange={(value) =>
                setEditingData({ ...editingData, caseIssuesUuid: value })
              }
              className="rounded-md border-gray-300 w-full h-[42px]"
              placeholder="Issue type"
              options={caseIssuesOptions.map((issue) => ({
                label: issue.case,
                value: issue.uuid,
              }))}
            />
          );
        }
        return record.caseIssues?.case ?? "-";
      },
    },
    {
      title: t("complaintDetailsSectionTableSubject"),
      dataIndex: "subject",
      key: "subject",
      width: 200,
      render: (_, record: any) => {
        if (isEditing(record)) {
          return (
            <Input
              value={editingData.subject}
              onChange={(e) =>
                setEditingData({ ...editingData, subject: e.target.value })
              }
              size="small"
              className="rounded-md border-gray-300"
              placeholder="Subject"
            />
          );
        }
        return record.subject ?? "-";
      },
    },
    {
      title: t("complaintDetailsSectionTablePriority"),
      dataIndex: "priority",
      key: "priority",
      width: 150,
      render: (_, record: any) => {
        const textColor = TinyColor(record.reportIssuePriorities?.color)
          .darken(60)
          .toString();

        if (isEditing(record)) {
          return (
            <Select
              value={editingData.reportIssuePrioritiesUuid}
              onChange={(value) =>
                setEditingData({
                  ...editingData,
                  reportIssuePrioritiesUuid: value,
                })
              }
              className="rounded-md border-gray-300 w-full h-[42px]"
            >
              {reportIssuePrioritiesOptions.map((priority) => (
                <Option key={priority.uuid} value={priority.uuid}>
                  {priority.companyPriority ?? priority.priority ?? "-"}
                </Option>
              ))}
            </Select>
          );
        }
        return (
          <span
            style={{
              background: record.reportIssuePriorities?.color,
              color: textColor,
              borderRadius: 8,
              padding: "2px 10px",
              fontSize: 12,
              fontWeight: 600,
              display: "inline-block",
            }}
          >
            {record.reportIssuePriorities?.companyPriority ||
              record.reportIssuePriorities?.priority ||
              "-"}
          </span>
        );
      },
    },
    // {
    //   title: t("complaintDetailsSectionTableStatus"),
    //   dataIndex: "status",
    //   key: "status",
    //   width: 150,
    //   render: () => (
    //     <span
    //       className="inline-block px-2.5 py-0.5 text-xs font-semibold rounded-lg"
    //       style={{
    //         background:
    //           reportData.status === "Analysis finished" ? "#2563eb" : "#000",
    //         color: "#fff",
    //       }}
    //     >
    //       {getTranslatedStatus(reportData.status, t)}
    //     </span>
    //   ),
    // },
    // {
    //   title: t("complaintDetailsSectionTableDepartment"),
    //   dataIndex: "department",
    //   key: "department",
    //   width: 200,
    //   render: () => (
    //     <div className="flex items-center gap-2">
    //       <Building size={18} />
    //       <span>{t("complaintDetailsSectionTableNotAssigned")}</span>
    //     </div>
    //   ),
    // },
    {
      title: t("complaintDetailsSectionTableActions"),
      key: "action",
      width: 180,
      fixed: "right" as const,
      render: (_: any, record: any) => {
        const editable = isEditing(record);
        return editable ? (
          <Space>
            <PrimaryButton size="small" onClick={() => save(record)}>
              Save
            </PrimaryButton>
            <Button size="small" icon={<CloseOutlined />} onClick={cancel}>
              Cancel
            </Button>
          </Space>
        ) : (
          <Space>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => edit(record)}
              disabled={editingKey !== null}
            >
              Edit
            </Button>
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleNavigateToIssueDetails(record.uuid)}
            >
              View
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Summary and Complainant Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <div>
          <Card className="h-full bg-white-A700 shadow-md">
            <div className="flex flex-col items-start gap-2">
              <div className="flex items-center gap-2">
                <FileTextOutlined className="text-black text-base md:text-lg" />
                <span className="text-base md:text-lg font-semibold">
                  {t("complaintDetailsSectionComplaintSummary")}
                </span>
              </div>
              <div>
                <Text type="secondary" className="text-sm md:text-base">
                  {reportData.reportResume && reportData.reportResume !== ""
                    ? reportData.reportResume
                    : ""}
                </Text>
              </div>
            </div>
          </Card>
        </div>

        <div>
          <Card className="h-full bg-white-A700 shadow-md">
            <div className="flex flex-col items-start gap-2">
              <div className="flex items-center gap-2">
                <UserOutlined className="text-black text-base md:text-lg" />
                <span className="text-base md:text-lg font-bold">
                  {t("complaintDetailsSectionComplainant")}
                </span>
              </div>
              <div>
                <Text type="secondary" className="text-sm md:text-base">
                  {reportData.reportIssues?.[0]?.complaint &&
                  reportData.reportIssues?.[0]?.complaint !== ""
                    ? reportData.reportIssues?.[0]?.complaint
                    : ""}
                </Text>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Location and Date Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        <div>
          <Card className="h-full bg-white-A700 shadow-md">
            <div className="flex flex-col items-start gap-2">
              <div className="flex items-center gap-2">
                <EnvironmentOutlined className="text-black text-base md:text-lg" />
                <span className="text-base md:text-lg font-semibold">
                  {t("complaintDetailsSectionLocation")}
                </span>
              </div>
              <div>
                <Text type="secondary" className="text-sm md:text-base">
                  {reportData.reportIssues?.[0]?.location &&
                  reportData.reportIssues?.[0]?.location !== ""
                    ? reportData.reportIssues?.[0]?.location
                    : ""}
                </Text>
              </div>
            </div>
          </Card>
        </div>

        <div>
          <Card className="h-full bg-white-A700 shadow-md">
            <div className="flex flex-col items-start gap-2">
              <div className="flex items-center gap-2">
                <CalendarOutlined className="text-black text-base md:text-lg" />
                <span className="text-base md:text-lg font-semibold">
                  {t("complaintDetailsSectionDateReported")}
                </span>
              </div>
              <div>
                <Text type="secondary" className="text-sm md:text-base">
                  {reportData.createdAt && reportData.createdAt !== ""
                    ? formatDateByLanguage(reportData.createdAt)
                    : ""}
                </Text>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Issues Table */}

      <div className="flex justify-end w-full">
        <PrimaryButton onClick={() => setOpenModalNewIssue(true)}>
          {t("newIssue")}
        </PrimaryButton>
      </div>

      <div className="overflow-x-auto bg-white-A700 shadow-md p-3 rounded-lg">
        <Table
          columns={columns}
          dataSource={reportData.reportIssues}
          pagination={false}
          scroll={{ x: "max-content" }}
          bordered
          size="small"
          className="text-sm md:text-base"
          rowKey="uuid"
        />
      </div>

      <CreateNewReportIssueModal
        isOpen={openModalNewIssue}
        setIsOpen={setOpenModalNewIssue}
        handleSave={handleCreateNewReportIssue}
        form={form}
      />
    </div>
  );
};

export default ComplaintDetailsSection;
